import { Survey, SurveyComponent, useSurvey } from "@tree-company/tree-query";
import { Navigate, useParams, useSearchParams } from "react-router";
import CATEGORIES from "../data/categories";
import translate from "../locales";
import { useEffect, useMemo } from "react";
import SurveyPage from "../components/survey_page";
import {
  AddConsequenceSection,
  ConsequenceQuestion,
} from "../components/consequences";
import Post from "../components/post";
import { buildPostFromStore } from "../helpers/post_helper";
import SheetPage from "../components/sheet_page";
import { getRandomPoster, getRandomTheme } from "../helpers/utils";
import TitleQuestion from "../components/generate_title";
import { CollectClient } from "@tree-company/collect-js-client";
import NL_TRANSLATIONS from "@tree-company/tree-query/locales/nl";

export const MIN_CONSEQUENCES = 4;
export const MAX_CONSEQUENCES = 20;

let collectorURL = import.meta.env.VITE_COLLECTOR_URL;
if (collectorURL === undefined && import.meta.env.DEV) {
  collectorURL = "http://localhost:3000";
}

function buildOptions(category, key) {
  return CATEGORIES[category][key].map((value) => ({
    value,
    label: { nl: translate(["categories", category, key, value]) },
    htmlAttributes: {
      class: "option--button",
    },
  }));
}

function buildConsequencesGroup(baseKey, showIf = null) {
  const amountKey = `${baseKey}_amount`;
  const sections = Array.from({ length: MAX_CONSEQUENCES }, (_, index) => ({
    type: "string",
    key: `${baseKey}_${index + 1}`,
    title: {
      // We show the label with a `*` if the questions is required
      nl: `${translate(["consequence", "label"], { index: index + 1 })}${index < MIN_CONSEQUENCES ? "*" : ""}`,
    },
    placeholder: {
      nl: translate(["consequence", "placeholder"]),
    },
    order: index,
    maxLength: 100,
    required: index < MIN_CONSEQUENCES,
    dependsOn: [amountKey],
    component: ConsequenceQuestion,
    skipIf: (t) => index >= t.getAnswer(amountKey),
  }));

  return {
    type: "group",
    key: baseKey,
    title: {
      nl: translate([baseKey, "group", "title"]),
    },
    order: 2,
    dependsOn: [showIf],
    skipIf: (t) => {
      if (showIf === null) return false;

      return !t.getAnswer(showIf);
    },
    sections: [
      ...sections.flat(),
      {
        type: "content",
        key: amountKey,
        component: AddConsequenceSection,
      },
    ],
  };
}

function buildChooseConsequence(key) {
  const optionKeys = Array.from(
    { length: MAX_CONSEQUENCES },
    (_, i) => `${key}_${i + 1}`,
  );
  return {
    type: "radio",
    key: `${key}_choose`,
    title: { nl: translate(`${key}_choose.label`) },
    required: true,
    order: 1,
    dependsOn: optionKeys,
    options: (t) =>
      optionKeys
        .map((optionKey) => {
          const answer = t.getAnswer(optionKey);
          if (answer) {
            return {
              value: answer,
              label: { nl: answer },
            };
          }
        })
        .filter((o) => !!o),
  };
}

function buildSurvey(category, fragment, groupCode) {
  const collectClient = new CollectClient({
    url: collectorURL,
    projectKey: CATEGORIES[category].collectorKey,
  });

  const treesponse = collectClient.initTreesponse({
    language: "nl",
    surveyVersion: "v2",
    initialAnswers: {
      fragment,
      category,
      groupCode,
      consequences_1_amount: MIN_CONSEQUENCES,
      consequences_2_amount: MIN_CONSEQUENCES,
      consequences_3_amount: MIN_CONSEQUENCES,
      posterImage: getRandomPoster(category),
      theme: getRandomTheme(),
    },
  });

  return new Survey(
    {
      key: "edubox-future-thinking",
      translations: NL_TRANSLATIONS,
      pages: [
        {
          component: SurveyPage,
          key: "age-and-perspective",
          order: 1,
          title: {
            nl: translate("ages_and_perspectives.page_title"),
          },
          description: {
            nl: translate("ages_and_perspectives.page_description"),
          },
          showProgress: true,
          sections: [
            {
              type: "group",
              key: "age-and-perspective",
              htmlAttributes: {
                class: "group--age-perspective",
              },
              sections: [
                {
                  type: "radio",
                  key: "perspectives",
                  order: 1,
                  title: {
                    nl: translate("ages_and_perspectives.perspectives.label"),
                  },
                  options: buildOptions(category, "perspectives"),
                  required: true,
                  htmlAttributes: {
                    class: "question--perspectives",
                  },
                },
                {
                  type: "radio",
                  key: "ages",
                  order: 2,
                  title: { nl: translate("ages_and_perspectives.ages.label") },
                  options: buildOptions(category, "ages"),
                  required: true,
                  htmlAttributes: {
                    class: "question--ages",
                  },
                },
              ],
            },
          ],
        },
        {
          component: SurveyPage,
          showProgress: true,
          key: "consequences_1",
          order: 2,
          title: { nl: null },
          sections: [buildConsequencesGroup("consequences_1")],
          steps: [translate("consequences_1.title")],
        },
        {
          component: SurveyPage,
          showProgress: true,
          key: "consequences_2",
          order: 3,
          title: { nl: null },
          sections: [
            buildChooseConsequence("consequences_1"),
            buildConsequencesGroup("consequences_2", "consequences_1_choose"),
          ],
          steps: [
            translate("consequences_1.title"),
            translate("consequences_2.title"),
          ],
        },
        {
          component: SurveyPage,
          showProgress: true,
          key: "consequences_3",
          order: 4,
          title: { nl: null },
          sections: [
            buildChooseConsequence("consequences_2"),
            buildConsequencesGroup("consequences_3", "consequences_2_choose"),
          ],
          steps: [
            translate("consequences_1.title"),
            translate("consequences_2.title"),
            translate("consequences_3.title"),
          ],
        },
        {
          component: SurveyPage,
          showProgress: true,
          key: "consequences_4",
          order: 5,
          title: { nl: null },
          sections: [buildChooseConsequence("consequences_3")],
          steps: [
            translate("consequences_1.title"),
            translate("consequences_2.title"),
            translate("consequences_3.title"),
            translate("consequences_4.title"),
          ],
        },
        {
          component: SheetPage,
          key: "consequences_path",
          order: 6,
          title: { nl: translate("consequences_path.title") },
          showNavigation: true,
          sections: [
            {
              type: "content",
              component: ({ content }) => (
                <Post
                  post={buildPostFromStore(content.store)}
                  page={3}
                  showNavigation={false}
                />
              ),
              order: 1,
              key: "consequences-path",
            },
          ],
        },
        {
          component: SurveyPage,
          key: "post-creation",
          order: 7,
          title: {
            nl: translate("createPost.title"),
          },
          description: {
            nl: (submission) =>
              translate("createPost.subtitle", {
                last_consequence: submission.getAnswer("consequences_3_choose"),
              }),
          },
          sections: [
            {
              type: "hidden",
              key: "completed",
              defaultValue: true,
            },
            {
              type: "string",
              key: "studentGroupName",
              order: 1,
              required: true,
              maxLength: 100,
              title: {
                nl: translate("createPost.fields.groupName.label"),
              },
              placeholder: {
                nl: translate("createPost.fields.groupName.placeholder"),
              },
            },
            {
              type: "group",
              key: "four-ws",
              order: 2,
              title: {
                nl: translate("createPost.fields.fourWs.instruction"),
              },
              sections: [
                {
                  type: "string",
                  key: "who",
                  order: 1,
                  required: true,
                  maxLength: 50,
                  title: {
                    nl: translate("createPost.fields.fourWs.who.label"),
                  },
                  placeholder: {
                    nl: translate("createPost.fields.fourWs.who.placeholder"),
                  },
                },
                {
                  type: "text",
                  key: "what",
                  order: 2,
                  required: true,
                  maxLength: 200,
                  rows: 3,
                  title: {
                    nl: translate("createPost.fields.fourWs.what.label"),
                  },
                  placeholder: {
                    nl: translate("createPost.fields.fourWs.what.placeholder"),
                  },
                },
                {
                  type: "string",
                  key: "where",
                  order: 3,
                  required: true,
                  maxLength: 50,
                  title: {
                    nl: translate("createPost.fields.fourWs.where.label"),
                  },
                  placeholder: {
                    nl: translate("createPost.fields.fourWs.where.placeholder"),
                  },
                },
                {
                  type: "text",
                  key: "why",
                  order: 4,
                  required: true,
                  maxLength: 200,
                  rows: 3,
                  title: {
                    nl: translate("createPost.fields.fourWs.why.label"),
                  },
                  placeholder: {
                    nl: translate("createPost.fields.fourWs.why.placeholder"),
                  },
                },
              ],
            },
            {
              type: "string",
              key: "title",
              order: 3,
              required: true,
              maxLength: 100,
              title: {
                nl: translate("createPost.fields.title.label"),
              },
              placeholder: {
                nl: translate("createPost.fields.title.placeholder"),
              },
              component: TitleQuestion,
            },
          ],
        },
        {
          component: SheetPage,
          key: "visualization",
          order: 8,
          title: { nl: translate("visualization.title") },
          sections: [
            {
              type: "content",
              key: "visualization",
              component: ({ content }) => (
                <Post post={buildPostFromStore(content.store)} />
              ),
              order: 1,
            },
          ],
        },
      ],
    },
    treesponse,
  );
}

export default function StudentSurvey() {
  const { category, fragment } = useParams();
  const [searchParams] = useSearchParams();
  const groupCode = searchParams.get("group")?.trim();

  if (CATEGORIES[category]?.fragments[fragment] === undefined) {
    return <Navigate to="/" />;
  }

  if (!groupCode) {
    return <Navigate to={`/${category}`} />;
  }

  const survey = useMemo(
    () => buildSurvey(category, fragment, groupCode),
    [category, fragment, groupCode],
  );

  const { currentPage } = useSurvey(survey);

  useEffect(() => {
    if (window.fathom) {
      window.fathom.trackPageview({
        url: `${window.location.href}/${currentPage.key}`,
      });
    }
  }, [currentPage]);

  return <SurveyComponent survey={survey} />;
}
